/* <gr-variant-state> — the single source of truth for which variant and which
   selling plan are selected.

   Everything that shows a price or adds to cart (buy box, sticky bar, timeline
   CTA, guarantee CTA) listens to this element instead of computing its own
   answer, so the theme can never show two different prices for one selection.

   All money is handled in integer subunits. There is no float arithmetic and no
   `price * 0.8` anywhere — a subscription price is whatever Shopify's selling
   plan allocation says it is. */

class GrVariantState extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    const payload = this.querySelector('[data-variant-data]');
    this.data = payload ? JSON.parse(payload.textContent) : { variants: [] };

    this.onChange = this.onChange.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.addEventListener('change', this.onChange);
    window.addEventListener('popstate', this.onPopState);

    // A ?variant= in the URL wins over the server's default selection, even if
    // that variant is unavailable — the shopper asked for it explicitly.
    const requested = new URLSearchParams(window.location.search).get('variant');
    const initial = (requested && this.find(requested)) || this.find(this.getAttribute('data-selected')) || this.data.variants[0];
    this.select(initial, { silent: false, push: false });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    window.removeEventListener('popstate', this.onPopState);
    this._bound = false;
  }

  find(id) {
    if (!id) return null;
    return this.data.variants.find((v) => String(v.id) === String(id)) || null;
  }

  get current() { return this._current || null; }

  get sellingPlanId() { return this._sellingPlanId || null; }

  /* The allocation for the current variant + plan, or null for a one-time buy. */
  get allocation() {
    if (!this._current || !this._sellingPlanId) return null;
    return (this._current.selling_plan_allocations || [])
      .find((a) => String(a.selling_plan_id) === String(this._sellingPlanId)) || null;
  }

  /* What the shopper actually pays right now, in subunits. */
  get effectivePrice() {
    const allocation = this.allocation;
    return allocation ? allocation.price : (this._current ? this._current.price : 0);
  }

  /* The merchandising reference (RRP) — the variant's compare-at, never the
     allocation's compare-at, which is a different concept. */
  get referencePrice() {
    if (!this._current) return 0;
    const compare = this._current.compare_at_price || 0;
    return compare > this._current.price ? compare : 0;
  }

  onChange(event) {
    const target = event.target;

    if (target.matches('[data-variant-option]')) {
      const variant = this.find(target.value);
      if (variant) this.select(variant, { push: true });
      return;
    }

    if (target.matches('[data-selling-plan]')) {
      this._sellingPlanId = target.checked ? target.value : null;
      this.sync();
      this.emit();
    }
  }

  onPopState() {
    const requested = new URLSearchParams(window.location.search).get('variant');
    const variant = this.find(requested) || this.data.variants[0];
    this.select(variant, { push: false });
  }

  select(variant, opts) {
    if (!variant) return;
    const options = opts || {};
    this._current = variant;

    // A plan that does not exist for the new variant must not survive the switch.
    if (this._sellingPlanId && !this.allocation) this._sellingPlanId = null;

    if (options.push !== false) this.pushUrl(variant);
    this.sync();
    if (options.silent !== true) this.emit();
  }

  /* Keep pathname, locale prefix and unrelated query params intact — rewriting
     the whole URL would drop a shopper out of their market. */
  pushUrl(variant) {
    if (!window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variant: variant.id }, '', url.toString());
  }

  sync() {
    const variant = this._current;
    if (!variant) return;

    this.querySelectorAll('[data-variant-option]').forEach((input) => {
      input.checked = String(input.value) === String(variant.id);
    });

    const idField = this.querySelector('[data-variant-id-field]');
    if (idField) idField.value = variant.id;

    const planField = this.querySelector('[data-selling-plan-field]');
    if (planField) planField.value = this._sellingPlanId || '';
  }

  emit() {
    this.dispatchEvent(new CustomEvent('gr:variant:change', {
      bubbles: true,
      detail: {
        variant: this._current,
        sellingPlanId: this._sellingPlanId,
        allocation: this.allocation,
        effectivePrice: this.effectivePrice,
        referencePrice: this.referencePrice
      }
    }));
  }
}

if (!customElements.get('gr-variant-state')) customElements.define('gr-variant-state', GrVariantState);

/* Any element with [data-gr-price] re-renders itself from the shared state.
   One listener, one formatter — no second pricing engine. */
document.addEventListener('gr:variant:change', function (event) {
  const { variant, effectivePrice, referencePrice } = event.detail;

  document.querySelectorAll('[data-gr-price]').forEach(function (el) {
    const kind = el.getAttribute('data-gr-price');
    if (kind === 'effective') el.textContent = GR.money(effectivePrice);
    if (kind === 'reference') {
      el.textContent = referencePrice ? GR.money(referencePrice) : '';
      el.hidden = !referencePrice;
    }
    if (kind === 'saving') {
      // The badge quotes the saving as a percentage of the reference price; the
      // trailing % sign comes from the localised string around this span.
      const saving = referencePrice ? referencePrice - effectivePrice : 0;
      const percent = saving > 0 ? Math.round((saving / referencePrice) * 100) : 0;
      el.textContent = percent > 0 ? String(percent) : '';
      el.hidden = percent <= 0;
    }
    if (kind === 'per-bottle') {
      const bottles = variant && variant.bottle_count ? variant.bottle_count : 0;
      // Integer division in subunits, matching the source's flooring.
      el.textContent = bottles > 0 ? GR.money(Math.floor(effectivePrice / bottles)) : '';
      el.hidden = bottles <= 0;
    }
  });

  document.querySelectorAll('[data-add-to-cart]').forEach(function (button) {
    const available = variant && variant.available;
    button.disabled = !available;
    const label = button.querySelector('[data-add-to-cart-label]');
    if (!label) return;
    if (!variant) label.textContent = GR.strings.unavailable;
    else if (!variant.available) label.textContent = GR.strings.soldOut;
    else label.textContent = button.getAttribute('data-label-template')
      ? button.getAttribute('data-label-template').replace('[price]', GR.money(effectivePrice))
      : GR.strings.addToCart;
  });
});
